/**
 * combat — pure, headless resolution for the COMBAT archetype's player-owned attack
 * hitbox (the one genuinely new primitive). World (Pixi-coupled) owns the live player +
 * entity list and the SIDE EFFECTS (killEntity, the pogo bounce); this module owns the
 * pure DECISION — which entities a live hitbox hits and whether the strike "connected"
 * (→ bounce / air-refresh). Mirrors the buffs.ts / clearConditions.ts split so the DoD
 * logic is unit-testable with NO Pixi.
 *
 * Determinism: integer-only AABB overlap + a Set lookup — no transcendentals / random /
 * wall-clock. ESLint-clean under src/engine/.
 */
import type { Entity } from './Entity';
import type { AABB } from './TileGrid';
import { KILLABLE_ENEMY_TYPES } from './clearConditions';

/** AABB overlap (top-left px). Half-open so flush edges don't count as overlap. */
function overlap(a: AABB, b: AABB): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Can a player attack hitbox kill this LIVE entity? The SAME set a stomp kills (Isaac,
 * 2026-06-30): the stompable enemy types, minus a per-entity opt-out — a spiked WALKER
 * reports `damageable()=false`, so the sword can't kill what a stomp can't. Hazards /
 * blocks / pickups (not in the set) are never sword-killable.
 */
export function isAttackKillable(e: Entity): boolean {
  if (!KILLABLE_ENEMY_TYPES.has(e.type)) return false;
  return e.damageable ? e.damageable() : true;
}

/** The pure result of an attack-hitbox pass over the entity list. */
export interface AttackResolution {
  /** Damageable enemies the hitbox overlaps — World routes each through ctx.killEntity. */
  kills: Entity[];
  /**
   * Brief 014 P1: `attackBreakable` SOLIDS (a CRACKED WALL) the hitbox overlaps — World
   * routes each through ctx.killEntity too (same non-solid path), on ANY strike direction.
   */
  breaks: Entity[];
  /**
   * Did the strike land on a valid target (→ the Player should bounce / refresh / recoil)?
   * True for any killable enemy hit, a broken wall, OR a `pogoable` hazard hit ON A DOWN
   * STRIKE (the pogo gesture — a side/horizontal swing into a spike does NOT bounce).
   */
  connected: boolean;
}

/**
 * Resolve a live attack/dash hitbox against the entity list. Pure: applies NO kills (World
 * owns the side effects via the returned list) and never mutates an entity. A null hitbox
 * (no active strike) resolves to nothing.
 *
 * @param hitbox    the player's current attack hitbox, or null when no strike is live
 * @param attackDir the strike aim ('down' enables the pogo-off-hazard rule)
 * @param entities  the live entity list
 */
export function resolveAttack(
  hitbox: AABB | null,
  attackDir: string | null,
  entities: readonly Entity[],
): AttackResolution {
  const kills: Entity[] = [];
  const breaks: Entity[] = [];
  if (!hitbox) return { kills, breaks, connected: false };
  const down = attackDir === 'down';
  let connected = false;
  for (const e of entities) {
    if (!e.alive || !overlap(hitbox, e.body)) continue;
    if (isAttackKillable(e)) {
      kills.push(e);
      connected = true;
    } else if (e.attackBreakable === true) {
      breaks.push(e); // Brief 014 P1 — a CRACKED WALL breaks on any strike direction
      connected = true;
    } else if (down && e.pogoable === true) {
      connected = true; // bounce off a climbable hazard — World does NOT kill it
    }
  }
  return { kills, breaks, connected };
}
